// Redacts secret-bearing values from Sentry events before they leave the process.
//
// Some API exchanges attach a request/response body as breadcrumb context (see the
// CitoApiV2 client's "Last Cito API Exchange"); the cPanel import wizard sends a
// source server's ROOT SSH password in that body. This `beforeSend` scrubber is the
// guaranteed catch-all: it runs on every captured event regardless of which code
// path set the context, and handles bodies whether they're plain objects or the
// JSON strings the fetch layer attaches.

const SENSITIVE_KEY =
  /pass(word)?|secret|token|api[-_]?key|authorization|private[-_]?key|credential/i;

export function redactSecrets(value: unknown): unknown {
  if (typeof value === "string") {
    // Bodies are often attached as a JSON string; redact inside it too.
    const t = value.trim();
    if (t.startsWith("{") || t.startsWith("[")) {
      try {
        return JSON.stringify(redactSecrets(JSON.parse(value)));
      } catch {
        // not JSON we can parse; leave it as-is
      }
    }
    return value;
  }
  if (Array.isArray(value)) return value.map(redactSecrets);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        SENSITIVE_KEY.test(k) ? "[redacted]" : redactSecrets(v),
      ]),
    );
  }
  return value;
}

// Scrub the parts of a Sentry event that can carry request/response payloads.
// Constrained to just the fields it touches so a Sentry `ErrorEvent` (which has no
// string index signature) satisfies it, while staying SDK-version agnostic.
type ScrubbableEvent = {
  contexts?: unknown;
  extra?: unknown;
  request?: unknown;
};
export function scrubSentryEvent<T extends ScrubbableEvent>(event: T): T {
  if (event.contexts)
    event.contexts = redactSecrets(event.contexts) as T["contexts"];
  if (event.extra) event.extra = redactSecrets(event.extra) as T["extra"];
  if (event.request)
    event.request = redactSecrets(event.request) as T["request"];
  return event;
}
